import { Component, useCallback, useMemo, useState } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { View } from 'react-native';
import { format, parse } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Text } from '@/components/ui/text';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { BaseModalProps } from '@/contexts/ModalContext';
import { Icon } from '~/global/components/icon/Icon';
import { dateFnsLocale } from '@/components/date-picker/DatePickerTrigger';
import i18n from 'i18next';
import { getLogger } from '@/lib/logger';
import { MealSlotGrouper } from '@/lib/local-api/meal/meal-slot-grouper';
import type {
  MealLogCopyGroup,
  MealLogCopyResult,
} from '@/lib/local-api/meal/meal-log-copy';

/** How many meal names a group line shows before collapsing into "+N more". */
const MAX_NAMES = 3;

const log = getLogger('MealLogCopySummaryModal');

export interface MealLogCopySummaryBodyProps {
  result: MealLogCopyResult;
  /**
   * Optional enrichment only. `Meal` is not synced (INV-0013), so names come
   * from GraphQL and may simply be absent offline — a missing name renders
   * **nothing**: not a spinner, not an error, not an empty bullet.
   */
  mealNamesById?: Record<string, string>;
}

/**
 * The per-destination lines. Exported separately so the modal's error boundary
 * wraps a swappable child — which is the seam the component spec uses to force
 * a render failure and prove the modal still offers a working Done (DD6).
 */
export const MealLogCopySummaryBody = ({
  result,
  mealNamesById,
}: MealLogCopySummaryBodyProps) => {
  const { t } = useTranslation('meal');
  // `i18n.language` is undefined until `initI18n` has run — which is not a
  // given for every consumer of this body (the component spec renders it bare).
  const locale = dateFnsLocale(i18n.language ?? 'en');

  const formatDay = useCallback(
    (day: string) => {
      try {
        return format(parse(day, 'yyyy-MM-dd', new Date()), 'd MMM', {
          locale,
        });
      } catch {
        // A malformed key must never take the modal down — the day string
        // itself is a perfectly readable fallback.
        return day;
      }
    },
    [locale],
  );

  return (
    <View className="gap-2" testID="meal-copy-summary-body">
      {result.groups.map((group: MealLogCopyGroup) => {
        // The slot is rendered through `slotOf`, NOT the raw `mealType`: a group
        // can legitimately carry `mealType: null` and must read "Snack" rather
        // than an empty fragment.
        const slot = MealSlotGrouper.slotOf(group);
        const names = group.mealIds
          .map((id) => mealNamesById?.[id])
          .filter((name): name is string => Boolean(name));

        return (
          <View
            key={`${group.day}-${group.mealType ?? 'null'}`}
            testID="meal-copy-summary-group"
            collapsable={false}
            className="gap-1 rounded-xl bg-surface-elevated px-4 py-3"
          >
            <View className="flex-row items-center gap-2">
              <Text className="text-sm font-inter-semibold text-text-primary">
                {t('copy.summary.line', { count: group.count })}
              </Text>
              <Icon name="ArrowRight" size={14} className="text-accent" />
              <Text className="flex-1 text-sm font-inter-semibold text-accent">
                {t('copy.summary.lineDestination', {
                  day: formatDay(group.day),
                  slot: t(`mealType.${slot.toLowerCase()}`),
                })}
              </Text>
            </View>
            {names.length > 0 ? (
              <Text className="text-xs text-text-muted">
                {[
                  ...names.slice(0, MAX_NAMES),
                  ...(names.length > MAX_NAMES
                    ? [
                        t('copy.summary.namesMore', {
                          count: names.length - MAX_NAMES,
                        }),
                      ]
                    : []),
                ].join(', ')}
              </Text>
            ) : null}
          </View>
        );
      })}
    </View>
  );
};

interface BodyBoundaryProps {
  fallback: ReactNode;
  children: ReactNode;
}

/**
 * Degrades the body to a count-only line rather than unmounting the modal.
 *
 * This covers **render** failures only. It deliberately does not — and cannot —
 * cover the Undo handler: boundaries never see event handlers or rejected
 * promises, which is why `handleUndo` is explicitly try/catch'ed instead.
 */
class SummaryBodyBoundary extends Component<
  BodyBoundaryProps,
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    log.error(
      { err: error, componentStack: info.componentStack },
      'Meal-log copy summary body failed to render; degrading to the count line',
    );
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

export interface MealLogCopySummaryModalProps extends BaseModalProps {
  result: MealLogCopyResult;
  mealNamesById?: Record<string, string>;
  /**
   * The revert. A **prop**, not an inlined `revertCopy` call — that is the seam
   * the component spec drives with a rejecting stub.
   */
  onUndo: () => Promise<void>;
  portalHost?: string;
}

/**
 * What landed where, plus a one-shot Undo.
 *
 * **A deliberate modal trap, built consciously (DD6).** `onOpenChange` ignores
 * everything, so backdrop taps, Escape and the Android hardware back gesture
 * are all swallowed, and there is no auto-timeout — a toast was rejected
 * precisely because it auto-dismisses, which would make the point of no return
 * incidental. Undo and Done are the only exits.
 *
 * Because there is no escape hatch, "the user can always leave" is guaranteed
 * by construction on both failure paths: the body renders inside an error
 * boundary that degrades to the count line, and Undo's handler is try/catch'ed
 * so a rejection re-enables the button and shows an inline error instead of
 * stranding the user. **Done is unconditional and always enabled** — it only
 * dismisses a modal whose work is already committed. Dismissing is terminal;
 * there is no re-entry surface, so do not build a reopen path.
 */
export const MealLogCopySummaryModal = ({
  onClose,
  result,
  mealNamesById,
  onUndo,
  portalHost,
}: MealLogCopySummaryModalProps) => {
  const { t } = useTranslation('meal');
  const [undoing, setUndoing] = useState(false);
  // Latched on SUCCESS only. Latching on any attempt would make a failed revert
  // permanently un-retryable — the exact trap DD6 calls out.
  const [hasReverted, setHasReverted] = useState(false);
  const [undoFailed, setUndoFailed] = useState(false);

  const total = useMemo(
    () => result.groups.reduce((sum, group) => sum + group.count, 0),
    [result.groups],
  );

  const handleUndo = useCallback(async () => {
    if (undoing || hasReverted) return;
    setUndoing(true);
    setUndoFailed(false);
    try {
      await onUndo();
      setHasReverted(true);
      onClose();
    } catch (error) {
      log.error({ err: error }, 'Undoing a meal-log copy/move failed');
      setUndoFailed(true);
    } finally {
      setUndoing(false);
    }
  }, [undoing, hasReverted, onUndo, onClose]);

  const degradedBody = (
    <View
      testID="meal-copy-summary-degraded"
      collapsable={false}
      className="gap-1 rounded-xl bg-surface-elevated px-4 py-3"
    >
      <Text className="text-sm font-inter-semibold text-text-primary">
        {t(`copy.summary.degradedLine`, {
          context: result.mode,
          count: total,
        })}
      </Text>
    </View>
  );

  return (
    <Dialog open onOpenChange={() => undefined}>
      <DialogContent portalHost={portalHost} hideClose>
        <DialogHeader>
          <DialogTitle>
            {t('copy.summary.title', { context: result.mode, count: total })}
          </DialogTitle>
          <DialogDescription>{t('copy.summary.description')}</DialogDescription>
        </DialogHeader>

        <SummaryBodyBoundary fallback={degradedBody}>
          <MealLogCopySummaryBody
            result={result}
            mealNamesById={mealNamesById}
          />
        </SummaryBodyBoundary>

        {undoFailed ? (
          <View
            testID="meal-copy-undo-error"
            collapsable={false}
            className="flex-row items-center gap-2 rounded-xl bg-negative/10 px-4 py-3"
          >
            <Icon name="TriangleAlert" size={16} className="text-negative" />
            <Text className="flex-1 text-xs text-negative">
              {t('copy.summary.undoError')}
            </Text>
          </View>
        ) : null}

        <DialogFooter>
          <Button
            testID="meal-copy-undo"
            variant="subtle"
            className="flex-1"
            disabled={undoing || hasReverted}
            onPress={() => void handleUndo()}
          >
            <Text>{t('copy.summary.undo')}</Text>
          </Button>
          <Button testID="meal-copy-done" className="flex-1" onPress={onClose}>
            <Text>{t('copy.summary.done')}</Text>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
